/// Low-level audio playback manager for the web client.
///
/// Owns the Howler interaction. Responsibilities:
///   - Stream an audio URL with engine-driven fade-in.
///   - Stop a stream with engine-driven fade-out.
///   - For looping streams: schedule a real self-crossfade at the loop
///     boundary so the splice is a smooth fade-out / fade-in (Howler's
///     native loop=true hard-cuts and clicks).
///
/// Does NOT know about cues, manifests, or game state — that's the
/// SoundtrackEngine's job. AudioManager is reusable for any future
/// audio source (voice clips, SFX, etc.).
///
/// Backed by Howler.js with `html5: true` for progressive streaming.

import { Howl } from 'howler';

const LOOP_CROSSFADE_POLL_MS = 1000;
const LOOP_CROSSFADE_MIN_LEAD_MS = 500;
/// Quick tail-fade applied to every one-shot stream (loop: false) so
/// Lyria's mid-note cutoff at the captured duration doesn't sound
/// abrupt. Loops are already handled by the loop-boundary
/// self-crossfade. Callers can override via PlayOptions.fadeOutAtEnd.
const DEFAULT_ONESHOT_TAIL_FADE_MS = 500;

/// Hard ceiling on concurrent streams. Every live Howl (html5: true) holds
/// one element from Howler's 10-slot HTML5 audio pool; rapid cue switching
/// (each switch = new stream + 5s crossfade of the old) can otherwise drain
/// the pool, after which Howler hands out locked audio objects that never
/// play: the "HTML5 Audio pool exhausted" wedge seen in production. The
/// engine's single-channel model needs at most 2 (new + outgoing crossfade);
/// 4 leaves headroom for overlapping fades before eviction kicks in. A loop
/// mid-splice briefly holds up to 3 pool elements (current + pending next +
/// fading old), but stop() clears loopTimer so evicted/fading streams can
/// never splice: at most ONE stream loops at a time under the single-channel
/// engine, bounding the true worst case at 3 + 3 fading singles, under 10.
const MAX_CONCURRENT_STREAMS = 4;

/// Bounded wait for a loop-splice Howl to load before treating the swap as
/// failed (see swapLoopHowl): a stalled HTML5 load fires no event at all.
const SWAP_LOAD_TIMEOUT_MS = 20_000;

/// Fade ramp tick interval (~60fps) for the volume sweep.
const FADE_TICK_MS = 16;
/// Exponential fade floor — the gain (relative to target) the outgoing
/// side decays to by the end of the ramp. 0.01 = -40 dB. A linear
/// amplitude ramp sits both tracks near -6 dB through the middle of a
/// crossfade (muddy); this exponential curve drops the outgoing track
/// fast so the "both audible" window collapses. Same shape mirrored for
/// fade-in vs fade-out. Howler's native `.fade()` is linear-only, so we
/// step the volume ourselves (see `rampVolume`).
const EXP_FADE_FLOOR = 0.01;

/// Exponential crossfade gain at normalized progress `p` ∈ [0,1].
/// Mirrored by direction: fade-out (`to < from`) decays fast; fade-in
/// (`to > from`) stays quiet then rises. Endpoints hit `from`/`to`
/// exactly.
function expFadeValue(from: number, to: number, p: number): number {
  const c = EXP_FADE_FLOOR;
  if (to < from) {
    const frac = (Math.pow(c, p) - c) / (1 - c);
    return to + (from - to) * frac;
  }
  const frac = (Math.pow(c, 1 - p) - c) / (1 - c);
  return from + (to - from) * frac;
}

export interface PlayOptions {
  /// URL of the audio source (any browser-supported format; M4A by default).
  url: string;
  /// Target volume (0..1) after fade-in completes.
  volume: number;
  /// Fade-in duration in ms. 0 = no fade-in.
  fadeInMs?: number;
  /// If true, the stream loops with a self-crossfade at the loop
  /// boundary. Looping uses `loopCrossfadeMs` for the splice.
  loop?: boolean;
  /// Crossfade duration at the loop boundary. Ignored if `loop` is false.
  loopCrossfadeMs?: number;
  /// Optional: an existing handle to crossfade with. The old handle
  /// fades out over `fadeInMs` while the new one fades in.
  crossfadeWith?: AudioStreamHandle | null;
  /// Optional: scheduled fade-out near the natural end of the audio
  /// (used by one-shot stings). The manager schedules a fade from the
  /// target volume to 0 starting `fadeOutAtEnd.fadeOutMs` before the
  /// stream ends.
  fadeOutAtEnd?: { fadeOutMs: number };
  /// Fired once the stream finishes naturally (only for `loop: false`).
  /// Not called when the stream is `stop()`'d. Use for "play FX then
  /// resume soundtrack" handoff.
  onEnd?: () => void;
}

export interface AudioStreamHandle {
  /// Stable id assigned at creation.
  readonly id: number;
  /// Source URL.
  readonly url: string;
  /// Whether this stream loops with self-crossfade.
  readonly loop: boolean;
  /// Currently-set target volume (changes as fades complete).
  volume: number;
  /// Whether the handle is alive (not yet disposed).
  alive: boolean;
}

interface ManagedStream extends AudioStreamHandle {
  howl: Howl;
  loopTimer: ReturnType<typeof setTimeout> | null;
  endFadeTimer: ReturnType<typeof setTimeout> | null;
  fadeTimer: ReturnType<typeof setInterval> | null;
  fadeInMs: number;
  loopCrossfadeMs: number;
  /// True once `stop()` has begun the fade-out. `setVolume` ignores
  /// streams in this state — the volume only goes one way (to zero).
  stopping: boolean;
}

let _nextId = 1;

export class AudioManager {
  private streams = new Set<ManagedStream>();

  /// Play an audio stream. Returns a handle the caller stores so they
  /// can `stop()` it later. The handle is also passed to the next
  /// `play()` call's `crossfadeWith` for a seamless transition.
  play(opts: PlayOptions): AudioStreamHandle {
    const fadeInMs = opts.fadeInMs ?? 0;
    const loop = opts.loop ?? false;
    const loopCrossfadeMs = opts.loopCrossfadeMs ?? fadeInMs;

    const howl = new Howl({
      src: [opts.url],
      html5: true, // progressive streaming
      format: ['m4a'],
      // Engine-driven loop crossfade — never use Howler's native loop.
      loop: false,
      volume: 0,
    });

    const stream: ManagedStream = {
      id: _nextId++,
      url: opts.url,
      loop,
      volume: opts.volume,
      alive: true,
      howl,
      loopTimer: null,
      endFadeTimer: null,
      fadeTimer: null,
      fadeInMs,
      loopCrossfadeMs,
      stopping: false,
    };
    this.streams.add(stream);

    // Evict oldest streams past the cap (hard cut, no fade: anything this
    // deep in the overlap stack is already fading out or leaked).
    while (this.streams.size > MAX_CONCURRENT_STREAMS) {
      let oldest: ManagedStream | null = null;
      for (const s of this.streams) {
        if (s !== stream && (oldest === null || s.id < oldest.id)) oldest = s;
      }
      if (!oldest) break;
      this.dispose(oldest);
    }

    const onLoaded = () => {
      if (!stream.alive) return;
      // Fade in (exponential curve via rampVolume).
      if (fadeInMs > 0) {
        stream.fadeTimer = this.rampVolume(howl, 0, opts.volume, fadeInMs, {
          shouldAbort: () => !stream.alive,
        });
      } else {
        howl.volume(opts.volume);
      }

      // Schedule a fade-out near natural end. For non-looping streams
      // this is on BY DEFAULT (Lyria captures cut off mid-note, which
      // is audibly abrupt without a quick tail fade). Callers can
      // override with their own fadeOutMs or explicitly opt out via
      // `fadeOutAtEnd: { fadeOutMs: 0 }`.
      const effectiveTailFade =
        opts.fadeOutAtEnd ?? (loop ? null : { fadeOutMs: DEFAULT_ONESHOT_TAIL_FADE_MS });
      if (effectiveTailFade && effectiveTailFade.fadeOutMs > 0) {
        const dur = howl.duration();
        if (dur && isFinite(dur) && dur > 0) {
          const fadeOutStartMs = Math.max(0, dur * 1000 - effectiveTailFade.fadeOutMs);
          stream.endFadeTimer = setTimeout(() => {
            if (stream.alive) {
              // Cancel any still-running fade-in (one-shots whose
              // duration < fadeIn + fadeOut) so the tail fade doesn't
              // fight a live fade-in interval over howl.volume().
              if (stream.fadeTimer) clearInterval(stream.fadeTimer);
              stream.fadeTimer = this.rampVolume(
                howl,
                stream.volume,
                0,
                effectiveTailFade.fadeOutMs,
                { shouldAbort: () => !stream.alive }
              );
            }
          }, fadeOutStartMs);
        }
      }

      // Schedule loop crossfade for looping streams.
      if (loop) {
        this.scheduleLoopCrossfade(stream);
      }
    };
    howl.once('load', onLoaded);

    const onOneShotDone = () => {
      // Sting / one-shot finished (or failed) — clean up and fire user callback.
      const cb = opts.onEnd;
      this.dispose(stream);
      if (cb) {
        try {
          cb();
        } catch (e) {
          console.warn('[audio-manager] onEnd callback threw', e);
        }
      }
    };

    howl.once('end', () => {
      if (stream.alive && !stream.loop) onOneShotDone();
    });

    // A failed one-shot Howl never fires `end`, so an `onEnd` caller
    // (SoundtrackEngine.handleFxEnd, which returns the engine to `soundtrack`
    // mode) never runs — the engine stays wedged in `fx` mode and silently
    // swallows every later soundtrack cue (post-combat music never resumes).
    // Treat a load/play failure as an end so the cleanup still fires and the
    // engine recovers.
    const onFxError = (kind: string) => () => {
      if (!stream.alive) return;
      console.warn(
        `[audio-manager] howl ${kind} — treating one-shot as ended so the audio engine recovers`
      );
      onOneShotDone();
    };
    howl.once('loaderror', onFxError('loaderror'));
    howl.once('playerror', onFxError('playerror'));

    howl.play();

    // Crossfade with the caller's previous handle.
    if (opts.crossfadeWith) {
      this.stop(opts.crossfadeWith, fadeInMs);
    }

    return stream;
  }

  /// Set the running volume of a stream (immediate or fade). No-op if
  /// the stream is already stopping — volume only goes one way during
  /// fade-out so a slider nudge mid-stop doesn't fight the fade and
  /// doesn't accidentally dispose the wrong stream via Howler's
  /// shared `'fade'` event.
  setVolume(handle: AudioStreamHandle, v: number, fadeMs = 0): void {
    const stream = handle as ManagedStream;
    if (!stream.alive || stream.stopping) return;
    const clamped = Math.min(1, Math.max(0, v));
    stream.volume = clamped;
    if (fadeMs > 0) {
      const cur = stream.howl.volume() as number;
      if (stream.fadeTimer) clearInterval(stream.fadeTimer);
      stream.fadeTimer = this.rampVolume(stream.howl, cur, clamped, fadeMs, {
        shouldAbort: () => !stream.alive || stream.stopping,
      });
    } else {
      stream.howl.volume(clamped);
    }
  }

  /// Stop a stream with optional fade-out, then dispose.
  ///
  /// Race-safe against concurrent `setVolume`: Howler emits `fade`
  /// for EVERY completed fade, so a once-listener registered here
  /// would fire on the setVolume's ramp (e.g., user nudges the volume
  /// slider mid-stop) and dispose the stream while the stop's fade is
  /// still in flight. To avoid that, we:
  ///   1. Set `stopping = true` on the stream so subsequent setVolume
  ///      calls become no-ops (the channel is on its way out).
  ///   2. Use setTimeout for disposal instead of `once('fade')` —
  ///      survives concurrent fades, fires at the right time.
  stop(handle: AudioStreamHandle, fadeOutMs = 0): void {
    const stream = handle as ManagedStream;
    if (!stream.alive) return;
    if (stream.loopTimer) {
      clearTimeout(stream.loopTimer);
      stream.loopTimer = null;
    }
    if (stream.endFadeTimer) {
      clearTimeout(stream.endFadeTimer);
      stream.endFadeTimer = null;
    }
    if (stream.fadeTimer) {
      clearInterval(stream.fadeTimer);
      stream.fadeTimer = null;
    }
    stream.stopping = true;
    const cur = stream.howl.volume() as number;
    if (cur <= 0 || fadeOutMs <= 0) {
      this.dispose(stream);
      return;
    }
    // Exponential fade-out, then dispose when the ramp completes. The
    // ramp owns its own interval (not a Howler `fade` event), so a
    // concurrent setVolume can't dispose this stream mid-stop.
    stream.fadeTimer = this.rampVolume(stream.howl, cur, 0, fadeOutMs, {
      shouldAbort: () => !stream.alive,
      onDone: () => this.dispose(stream),
    });
  }

  /// Stop and dispose every active stream. Used on engine reset / sign-out.
  stopAll(fadeOutMs = 0): void {
    for (const stream of [...this.streams]) {
      this.stop(stream, fadeOutMs);
    }
  }

  /// Schedule a self-crossfade at the loop boundary. Replaces the
  /// stream in-place: the handle stays valid, the underlying Howl is
  /// swapped at splice time.
  private scheduleLoopCrossfade(stream: ManagedStream): void {
    const arm = () => {
      if (!stream.alive) return;
      const dur = stream.howl.duration();
      if (!dur || !isFinite(dur) || dur <= 0) {
        stream.loopTimer = setTimeout(arm, LOOP_CROSSFADE_POLL_MS);
        return;
      }
      const crossfadeStartMs = Math.max(
        LOOP_CROSSFADE_MIN_LEAD_MS,
        dur * 1000 - stream.loopCrossfadeMs
      );
      stream.loopTimer = setTimeout(() => {
        if (!stream.alive) return;
        this.swapLoopHowl(stream);
      }, crossfadeStartMs);
    };
    arm();
  }

  /// Splice the loop: create a fresh Howl on the same URL, fade it in
  /// while fading out the old, then swap the stream's underlying howl
  /// pointer. Re-arms the next loop crossfade after the swap.
  private swapLoopHowl(stream: ManagedStream): void {
    // A stream already on its way out must not splice: the swap would
    // clear the stop() fade whose onDone is the ONLY dispose path,
    // resurrecting a stopped stream as an unstoppable zombie loop.
    if (!stream.alive || stream.stopping) return;
    const next = new Howl({
      src: [stream.url],
      html5: true,
      format: ['m4a'],
      loop: false,
      volume: 0,
    });
    const fadeMs = stream.loopCrossfadeMs;
    // One outcome only: load, error, or the watchdog below, whichever
    // lands first. `next` is not in `streams` until the swap completes,
    // so every exit path here must account for it or it leaks a pooled
    // HTML5 audio element.
    let settled = false;
    let swapWatchdog: ReturnType<typeof setTimeout> | null = null;
    const settle = (): boolean => {
      if (settled) return false;
      settled = true;
      if (swapWatchdog) clearTimeout(swapWatchdog);
      return true;
    };
    next.once('load', () => {
      if (!settle()) return;
      // `stopping` too, not just `alive`: a stop() racing this load has
      // its dispose ramp running, and clearing it below would leak the
      // stream (see entry guard).
      if (!stream.alive || stream.stopping) {
        try {
          next.unload();
        } catch {
          /* ignore */
        }
        return;
      }
      // Fade the new player in (exponential); tracked as the stream's
      // active fade so a racing stop() cancels it. Clear any live ramp
      // first (a fade-in still running when the track is shorter than
      // the fade) so two intervals never fight over howl.volume().
      if (stream.fadeTimer) clearInterval(stream.fadeTimer);
      stream.fadeTimer = this.rampVolume(next, 0, stream.volume, fadeMs, {
        shouldAbort: () => !stream.alive,
      });
      // Re-arm for the next loop boundary.
      stream.howl = next;
      this.scheduleLoopCrossfade(stream);
    });
    // A next-Howl that never loads would otherwise wedge the stream: the
    // old howl is already fading toward its natural end, the swap never
    // happens, and the handle stays `alive`, so the engine's same-URL
    // guard silently swallows every later cue and the soundtrack goes
    // permanently silent. Treat it as stream death: dispose so the next
    // `playSoundtrack` (server re-send, cue change, mute toggle) sees a
    // dead handle and rebuilds a fresh stream.
    const onSwapError = () => {
      if (!settle()) return;
      try {
        next.unload();
      } catch {
        /* ignore */
      }
      if (stream.alive && !stream.stopping) {
        console.warn('[audio-manager] loop-swap howl failed; disposing stream so it can recover');
        this.dispose(stream);
      }
    };
    next.once('loaderror', onSwapError);
    next.once('playerror', onSwapError);
    // Bounded escape for the third outcome: a load that never resolves
    // (stalled network keeps the handle alive and the same-URL guard
    // wedged with no event to break the tie).
    swapWatchdog = setTimeout(onSwapError, SWAP_LOAD_TIMEOUT_MS);
    next.play();

    // Fade out the old (exponential); unload when the ramp completes.
    // Add shouldAbort so a concurrent stopAll() (mid-crossfade audio-off)
    // terminates this dangling ramp — the old Howl would otherwise keep
    // fading to completion outside any tracked stream. (night-watch: bug #1)
    const old = stream.howl;
    this.rampVolume(old, old.volume() as number, 0, fadeMs, {
      shouldAbort: () => {
        if (!stream.alive) {
          try {
            old.unload();
          } catch {
            /* ignore */
          }
          return true;
        }
        return false;
      },
      onDone: () => {
        try {
          old.unload();
        } catch {
          /* ignore */
        }
      },
    });
  }

  /// Ramp a howl's volume from `from` to `to` over `ms` using the
  /// exponential crossfade curve (see `expFadeValue`). Returns the
  /// interval handle so callers can cancel it; fires `onDone` when the
  /// ramp completes naturally. `shouldAbort` is checked each tick — when
  /// true the interval clears without touching the howl (guards against
  /// ticks firing after the stream is disposed).
  private rampVolume(
    howl: Howl,
    from: number,
    to: number,
    ms: number,
    opts?: { onDone?: () => void; shouldAbort?: () => boolean }
  ): ReturnType<typeof setInterval> | null {
    if (ms <= 0) {
      if (opts?.shouldAbort?.()) return null;
      howl.volume(to);
      opts?.onDone?.();
      return null;
    }
    const ticks = Math.ceil(ms / FADE_TICK_MS);
    let i = 0;
    const timer = setInterval(() => {
      if (opts?.shouldAbort?.()) {
        clearInterval(timer);
        return;
      }
      i++;
      const p = Math.min(1, i / ticks);
      howl.volume(expFadeValue(from, to, p));
      if (i >= ticks) {
        clearInterval(timer);
        opts?.onDone?.();
      }
    }, FADE_TICK_MS);
    return timer;
  }

  private dispose(stream: ManagedStream): void {
    if (!stream.alive) return;
    stream.alive = false;
    if (stream.loopTimer) {
      clearTimeout(stream.loopTimer);
      stream.loopTimer = null;
    }
    if (stream.endFadeTimer) {
      clearTimeout(stream.endFadeTimer);
      stream.endFadeTimer = null;
    }
    if (stream.fadeTimer) {
      clearInterval(stream.fadeTimer);
      stream.fadeTimer = null;
    }
    try {
      stream.howl.unload();
    } catch {
      /* ignore */
    }
    this.streams.delete(stream);
  }
}

// Module-singleton — every caller imports the same instance.
let _instance: AudioManager | null = null;
export function getAudioManager(): AudioManager {
  if (!_instance) _instance = new AudioManager();
  return _instance;
}

// Test seam — replace the singleton (e.g., in test setup).
export function __setAudioManagerForTesting(mgr: AudioManager | null): void {
  _instance = mgr;
}
